// Reserved usernames that cannot be used by regular users
// These are reserved for system, brand, admin, or common service accounts

export const RESERVED_USERNAMES = new Set([
  // Brand names
  'filmple',
  'filmpl',
  'filmfinder',
  'film_finder',
  'film-finder',

  // Admin & support
  'admin',
  'administrator',
  'support',
  'help',
  'helpdesk',
  'mod',
  'moderator',
  'staff',
  'team',
  'official',

  // System accounts
  'system',
  'bot',
  'api',
  'root',
  'null',
  'undefined',
  'anonymous',
  'guest',
  'user',
  'users',

  // Common reserved
  'about',
  'account',
  'accounts',
  'activity',
  'auth',
  'billing',
  'blog',
  'contact',
  'dashboard',
  'delete',
  'deleted',
  'discover',
  'edit',
  'email',
  'explore',
  'faq',
  'feed',
  'feedback',
  'follow',
  'followers',
  'following',
  'home',
  'inbox',
  'info',
  'legal',
  'login',
  'logout',
  'me',
  'messages',
  'movie',
  'movies',
  'news',
  'notifications',
  'password',
  'popular',
  'post',
  'posts',
  'privacy',
  'profile',
  'profiles',
  'rating',
  'ratings',
  'register',
  'report',
  'search',
  'settings',
  'signup',
  'signin',
  'sitemap',
  'status',
  'subscribe',
  'terms',
  'trending',
  'tv',
  'unsubscribe',
  'update',
  'upload',
  'watchlist',
  'watchlists',
  'welcome',

  // Potentially offensive placeholders
  'test',
  'testing',
  'example',
  'demo',
  'sample',
]);

/**
 * Check if a username is reserved
 * @param username - The username to check (case-insensitive)
 * @returns true if reserved, false if available
 */
export function isReservedUsername(username: string): boolean {
  return RESERVED_USERNAMES.has(username.toLowerCase());
}

/**
 * Validate username format
 * Rules:
 * - 3-20 characters
 * - Only letters, numbers, underscores
 * - Cannot start or end with underscore
 * - Cannot have consecutive underscores
 * - Cannot be all numbers
 */
export function validateUsernameFormat(username: string): { valid: boolean; error?: string } {
  if (!username) {
    return { valid: false, error: 'Username is required' };
  }

  if (username.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' };
  }

  if (username.length > 20) {
    return { valid: false, error: 'Username must be 20 characters or less' };
  }

  if (!/^[a-zA-Z0-9_]+$/.test(username)) {
    return { valid: false, error: 'Username can only contain letters, numbers, and underscores' };
  }

  if (username.startsWith('_') || username.endsWith('_')) {
    return { valid: false, error: 'Username cannot start or end with underscore' };
  }

  if (username.includes('__')) {
    return { valid: false, error: 'Username cannot have consecutive underscores' };
  }

  if (/^\d+$/.test(username)) {
    return { valid: false, error: 'Username cannot be all numbers' };
  }

  return { valid: true };
}
